/**
 * Search Yardage Price Fix
 * Converts search result prices to per-yard for Fractional Yardage tagged products
 * Supports both Boost app rendering AND theme-native product cards
 * Version: 1.1.0 - 2026-03-24 - Added theme-native support
 */

(function() {
  'use strict';

  const CONFIG = {
    yardsToUnitsMultiplier: 2, // 1 yard = 2 units, so price * 2 = per-yard price
  };

  // Add CSS to hide instant search dropdown prices (user preference)
  const style = document.createElement('style');
  style.textContent = `
    /* Hide Boost instant search dropdown prices completely (user preference) */
    .boost-sd__instant-search-container [class*="price"],
    .boost-sd__suggestion-wrapper [class*="price"] {
      display: none !important;
    }

    /* Note: Theme-native product blocks already have correct prices from Liquid,
       but this script adds yardage suffix if missing for full search results page */
  `;
  document.head.appendChild(style);

  // Store product handles with Fractional Yardage tag (cached)
  const fractionalYardageProducts = new Set();
  const nonFractionalProducts = new Set(); // Cache products that DON'T have the tag
  const pendingFetches = new Map(); // Prevent duplicate fetches

  /**
   * Check if a product handle has the Fractional Yardage tag
   * Uses aggressive caching to minimize delays
   */
  async function checkProductTags(handle) {
    // Check cache first
    if (fractionalYardageProducts.has(handle)) {
      return true;
    }
    if (nonFractionalProducts.has(handle)) {
      return false;
    }

    // Check if we're already fetching this product
    if (pendingFetches.has(handle)) {
      return await pendingFetches.get(handle);
    }

    // Fetch product data
    const fetchPromise = (async () => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        const product = await response.json();

        // Check if product has "Fractional Yardage" tag
        const hasFractionalYardage = product.tags.some(tag =>
          tag.toLowerCase() === 'fractional yardage'
        );

        if (hasFractionalYardage) {
          fractionalYardageProducts.add(handle);
          return true;
        } else {
          nonFractionalProducts.add(handle);
          return false;
        }
      } catch (error) {
        console.error('[Boost Yardage Search] Error checking product tags:', error);
        nonFractionalProducts.add(handle); // Cache as non-fractional to avoid retry
        return false;
      } finally {
        pendingFetches.delete(handle); // Clean up
      }
    })();

    pendingFetches.set(handle, fetchPromise);
    return await fetchPromise;
  }

  /**
   * Extract product handle from product card (Boost OR theme)
   */
  function getProductHandle(productCard) {
    // Try to find product link - check multiple patterns
    let productLink = productCard.querySelector('a[href*="/products/"]');

    // If productCard itself is a link, use it
    if (!productLink && productCard.tagName === 'A' && productCard.href && productCard.href.includes('/products/')) {
      productLink = productCard;
    }

    // Try finding link in children
    if (!productLink) {
      productLink = productCard.querySelector('[href*="/products/"]');
    }

    // For theme product blocks, check data attribute
    if (!productLink && productCard.hasAttribute('data-product-id')) {
      productLink = productCard.querySelector('.product-link');
    }

    if (!productLink) {
      console.log('[Search Yardage] No product link found in:', productCard);
      return null;
    }

    const url = productLink.getAttribute('href') || productLink.href;
    const match = url.match(/\/products\/([^?#\/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Convert a product card's price to per-yard
   */
  async function convertProductPrice(productCard) {
    if (productCard.hasAttribute('data-yardage-converted')) {
      return; // Already converted
    }

    const handle = getProductHandle(productCard);
    if (!handle) {
      console.log('[Boost Yardage Search] No handle found for product card');
      return;
    }

    // Find price elements - try multiple selectors (Boost AND theme)
    let priceElements = productCard.querySelectorAll(
      '.boost-sd__suggestion-queries-item-price, ' +
      '.boost-sd__format-currency, ' +
      '.product-price__amount, ' +
      '[class*="price"]'
    );

    // Filter to only elements that contain a dollar sign
    priceElements = Array.from(priceElements).filter(el => el.textContent.includes('$'));

    // Check if this product has Fractional Yardage tag
    const hasFractionalYardage = await checkProductTags(handle);
    if (!hasFractionalYardage) {
      console.log('[Search Yardage] Product', handle, 'does not have Fractional Yardage tag');
      // Mark as checked so price shows (not fractional, no conversion needed)
      priceElements.forEach(el => el.setAttribute('data-yardage-checked', 'true'));
      return;
    }

    console.log('[Search Yardage] Converting price for', handle);

    priceElements.forEach(priceElement => {
      if (priceElement.hasAttribute('data-yardage-converted')) {
        return; // Already converted
      }

      const priceText = priceElement.textContent.trim();

      // Skip if already has "/yard" suffix (theme already converted)
      if (priceText.includes('/yard')) {
        priceElement.setAttribute('data-yardage-converted', 'true');
        console.log('[Search Yardage] Price already shows /yard for', handle);
        return;
      }

      // Extract ALL numeric prices from the text (Boost may show multiple)
      const allPrices = priceText.match(/[\d,]+\.?\d*/g);
      if (!allPrices || allPrices.length === 0) return;

      // Parse prices
      const prices = allPrices.map(p => parseFloat(p.replace(/,/g, '')));
      const uniquePrices = [...new Set(prices)].sort((a, b) => b - a); // Descending order

      // Check if this is a real sale (two truly different prices) vs Boost's duplicate display
      if (uniquePrices.length === 2) {
        // Check if one price is exactly 2x the other (Boost showing both unit and yard price)
        const higher = uniquePrices[0];
        const lower = uniquePrices[1];
        const isDoubleDisplay = Math.abs(higher - (lower * CONFIG.yardsToUnitsMultiplier)) < 0.01;

        if (isDoubleDisplay) {
          // Boost is showing both unit and per-yard price - just show per-yard
          priceElement.textContent = `$${higher.toFixed(2)}/yard`;
          console.log('[Search Yardage] Cleaned duplicate display for', handle, '- showing', `$${higher}/yard`);
        } else {
          // Real sale: convert both prices and preserve sale structure
          const compareYardPrice = higher * CONFIG.yardsToUnitsMultiplier;
          const saleYardPrice = lower * CONFIG.yardsToUnitsMultiplier;
          priceElement.innerHTML = `<span style="text-decoration: line-through;">$${compareYardPrice.toFixed(2)}</span> $${saleYardPrice.toFixed(2)}/yard`;
          console.log('[Search Yardage] Converted sale for', handle, '- from', `$${higher}/$${lower}`, 'to', `$${compareYardPrice}/$${saleYardPrice}/yard`);
        }
      } else {
        // Single price - convert to per-yard
        const unitPrice = uniquePrices[0];
        const yardPrice = unitPrice * CONFIG.yardsToUnitsMultiplier;
        priceElement.textContent = `$${yardPrice.toFixed(2)}/yard`;
        console.log('[Search Yardage] Converted', handle, 'from', `$${unitPrice}`, 'to', `$${yardPrice}/yard`);
      }

      priceElement.setAttribute('data-yardage-converted', 'true');
    });

    productCard.setAttribute('data-yardage-converted', 'true');
  }

  /**
   * Pre-fetch product tags for all visible products (speeds up conversion)
   */
  function prefetchProductTags() {
    // Find all product links in Boost containers AND theme product blocks
    const searchContainers = document.querySelectorAll(
      '.boost-sd__instant-search-container, ' +
      '.boost-sd__suggestion-wrapper, ' +
      '.search-results, ' +
      '.collection-listing'
    );
    const productLinks = new Set();

    searchContainers.forEach(container => {
      const links = container.querySelectorAll('[href*="/products/"]');
      links.forEach(link => {
        const url = link.getAttribute('href') || link.href;
        const match = url.match(/\/products\/([^?#\/]+)/);
        if (match) productLinks.add(match[1]);
      });
    });

    productLinks.forEach(handle => {
      if (!fractionalYardageProducts.has(handle) && !nonFractionalProducts.has(handle)) {
        // Start fetching in background (don't await)
        checkProductTags(handle);
      }
    });
  }

  /**
   * Convert all visible search result products (Boost OR theme)
   * Skips instant search dropdown (prices hidden by CSS per user preference)
   */
  async function convertSearchResults() {
    let productCards = [];

    // Skip instant search dropdown - prices are hidden there per user preference
    // Only process full search results page

    // Try theme-native product blocks (main-search.liquid) - PRIMARY
    productCards = Array.from(document.querySelectorAll('.product-block[data-product-id]'));

    // Fallback: try Boost selectors for full search page (if Boost is rendering results)
    if (productCards.length === 0) {
      productCards = Array.from(document.querySelectorAll('.boost-sd__suggestion-queries-item-product, .boost-sd__product-item, [class*="boost-sd__product"]'));
    }

    if (productCards.length === 0) {
      console.log('[Search Yardage] No product cards found on search results page');
      return;
    }

    console.log('[Search Yardage] Found', productCards.length, 'product cards on search results page');

    // Pre-fetch all product tags in parallel for faster conversion
    prefetchProductTags();

    // Convert each product card
    for (const card of productCards) {
      await convertProductPrice(card);
    }
  }

  /**
   * Watch for search results being added/updated (Boost OR theme)
   */
  function watchSearchResults() {
    const observer = new MutationObserver(function(mutations) {
      // Check if new products were added (Boost OR theme)
      const hasNewProducts = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === 1) { // Element node
            // Check for instant search container or product links
            if (node.classList) {
              if (node.classList.contains('boost-sd__instant-search-container') ||
                  node.classList.contains('boost-sd__suggestion-wrapper') ||
                  node.classList.contains('boost-sd__suggestion-queries-item-product') ||
                  node.classList.contains('boost-sd__product-item') ||
                  node.classList.contains('product-block') ||
                  node.classList.contains('product-list')) {
                return true;
              }
            }
            // Check if node contains product links
            if (node.querySelector) {
              return node.querySelector('[href*="/products/"], .boost-sd__instant-search-container, [class*="boost-sd__product"], .product-block');
            }
          }
          return false;
        });
      });

      if (hasNewProducts) {
        console.log('[Search Yardage] New products detected, converting...');
        setTimeout(convertSearchResults, 200); // Small delay to let rendering finish
      }
    });

    // Observe Boost containers AND theme search results
    const searchContainers = document.querySelectorAll(
      '.boost-sd__instant-search-container, ' +
      '.boost-sd__suggestion-wrapper, ' +
      '[class*="boost-sd"], ' +
      '.search-results, ' +
      '.collection-listing, ' +
      'main'
    );

    if (searchContainers.length > 0) {
      searchContainers.forEach(container => {
        observer.observe(container, {
          childList: true,
          subtree: true
        });
      });
      console.log(`[Search Yardage] Monitoring ${searchContainers.length} search containers`);
    } else {
      // Fallback: observe document.body but with guard to check target
      console.log('[Search Yardage] No search containers found yet, watching document.body with guards');
      observer.observe(document.body, {
        childList: true,
        subtree: false // Only watch direct children
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      watchSearchResults();
      convertSearchResults();
    });
  } else {
    watchSearchResults();
    convertSearchResults();
  }

  // Also try converting after a short delay (in case rendering is slow)
  setTimeout(convertSearchResults, 1000);
  setTimeout(convertSearchResults, 2000);

})();
